import { FC, useEffect } from 'react';
import XUITag from '@xero/xui/react/tag';
import { IAccount, IAccountSettings } from '../models';
import lang from '../static/lang/en-NZ';

export interface IDropDownButtonProps {
    accountId: string | null;
    defaultAccount: IAccountSettings;
    isLiability?: boolean;
    setAccount: (accountId: string) => void;
}

export const createButtonContent =
    (chartOfAccounts: IAccount[]): FC<IDropDownButtonProps> =>
    ({ accountId, defaultAccount, isLiability, setAccount, children }) => {
        const account = accountId
            ? chartOfAccounts.find((b) => b.accountId === accountId)
            : defaultAccount.defaultToSuggestion
            ? defaultAccount.suggestedAccount
            : null;

        // if the default account has an ID, set it
        useEffect(() => {
            if (!accountId && account?.accountId) {
                setAccount(account.accountId);
            }
        }, [accountId, account, setAccount]);

        const tags = [];
        if (isLiability) {
            tags.push(
                <XUITag className='xui-margin-left-small' variant='standard' key={lang.TAGS.LIABILITY}>
                    {lang.TAGS.LIABILITY}
                </XUITag>
            );
        }
        if (!account?.accountId) {
            tags.push(
                <XUITag className='xui-margin-left-small' variant='neutral' key={lang.TAGS.NEW}>
                    {lang.TAGS.NEW}
                </XUITag>
            );
        }
        return account ? (
            <span>
                {account.code} - {account.name}
                <>{tags}</>
            </span>
        ) : (
            <>{children}</>
        );
    };

const defaultGuid = '00000000-0000-0000-0000-000000000000';

export const formatAccountButtonContent = (
    accounts: IAccount[],
    currentAccountId: string | undefined | null,
    placeholderText: string
): JSX.Element => {
    const chartOfAccount = accounts?.find((coa) => coa.accountId === currentAccountId);
    const isNewRecommendedAccount = chartOfAccount ? chartOfAccount.accountId === defaultGuid : false;

    return isNewRecommendedAccount && chartOfAccount ? (
        <>
            <XUITag className={'xui-tag-xsmall'} variant='standard' key={lang.TAGS.NEW}>
                {lang.TAGS.NEW}
            </XUITag>
            <br />
            {chartOfAccount.code + ' - ' + chartOfAccount.name}
        </>
    ) : chartOfAccount ? (
        <>{chartOfAccount.code + ' - ' + chartOfAccount.name}</>
    ) : (
        <>{placeholderText}</>
    );
};

export const formatPickListItem = (item: IAccount): JSX.Element => {
    const tag = !item.accountId && (
        <XUITag className='xui-margin-left-small xui-margin-top-small' variant='neutral'>
            {lang.TAGS.NEW}
        </XUITag>
    );
    return (
        <span className='account-select-option'>
            <span className='xui-u-flex-inline'>
                <h4 className='account-select-option--header xui-margin-top-small'>
                    {item.code}-{item.name}
                </h4>
                {tag}
            </span>
            <p className='xui-text-secondary account-select-option--body'>{item.description}</p>
        </span>
    );
};

export const formatCoAListItem = (accountItem: IAccount): JSX.Element => {
    return (
        <div className='xui-margin-left-xsmall xui-margin-top-small'>
            {accountItem.accountId === defaultGuid ? (
                <XUITag className={'xui-tag-xsmall'} variant='standard' key={lang.TAGS.NEW}>
                    {lang.TAGS.NEW}
                </XUITag>
            ) : (
                <div />
            )}
            <div>
                <span className='xui-text-emphasis'>
                    {accountItem.code} - {accountItem.name}
                </span>
                <br />
                <span className='xui-text-secondary'>
                    {accountItem.class.charAt(0) + accountItem.class.slice(1).toLowerCase()}
                </span>
            </div>
        </div>
    );
};
